/**
 * 도움말 설명에 마우스를 올리면 **화면의 그 조각을 짚어 준다** (담당자 요청 2026-09-15).
 *
 * 도움말이 *"‘전일 배차 복사’ 를 누릅니다"* 라고 말해도 그 버튼이 화면 어디 있는지는
 * 여전히 눈으로 찾아야 했다. 드로어는 처음부터 **비모달**(`aria-modal="false"`)이고 뒤
 * 본문을 가리지 않는 투명 막을 쓴다 — 내용을 보며 읽으라고 만든 것이라, 짚어 주기에 맞다.
 *
 * **이름이 곧 선택자다.** 이 저장소는 `controls[].name` 을 화면에 그려지는 글자와
 * **정확히 같게** 적는 규칙이 있어(CLAUDE.md §도움말), 따로 선택자를 달지 않아도 된다.
 * 규칙이 깨지면 이 기능이 먼저 말해 준다 — 아무것도 안 짚히면 이름이 틀린 것이다.
 */

/** 짚을 수 있는 것들. 문단 전체가 아니라 **누르거나 읽는 조각**만 본다 */
const TARGETS = "button, a, th, label, summary, [role='button'], [role='tab']";

/** 화면에 그린 테두리. 정의는 `index.css` 의 `.hj-help-spot` */
const SPOT_CLASS = "hj-help-spot";

/**
 * `전체 N곳 선택` 처럼 **자리표시가 든 이름**도 짚는다.
 * `N` 은 숫자 자리이고, 나머지는 글자 그대로다.
 */
function toMatcher(label: string): RegExp {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^\\s*${escaped.replace(/N/g, "\\d+")}\\s*$`);
}

/**
 * 한 항목에 라벨이 여럿 적힌 것(`사용으로 / 미사용으로`, `계획톤수 · 실적톤수`)은
 * 나눠서 **각각** 짚는다. 문서가 묶어 적었을 뿐 화면에서는 따로 있다.
 */
function labelsOf(name: string): string[] {
  return name
    .split(/ \/ | · /)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** 지금 화면에서 그 이름에 해당하는 조각들. 없으면 빈 배열 */
export function findSpots(name: string): HTMLElement[] {
  const root = document.querySelector("main") ?? document.body;
  const matchers = labelsOf(name).map(toMatcher);
  const found: HTMLElement[] = [];

  root.querySelectorAll<HTMLElement>(TARGETS).forEach((el) => {
    // 드로어 안의 글자를 짚으면 안 된다 — 설명 자체가 잡힌다
    if (el.closest("[data-help-drawer]")) return;
    const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
    if (!text) return;
    if (matchers.some((re) => re.test(text))) found.push(el);
  });
  return found;
}

/**
 * 짚는다. 되돌리는 함수를 돌려준다.
 *
 * 첫 조각이 화면 밖이면 가운데로 굴린다 — 짚어 놓고 안 보이면 짚지 않은 것과 같다.
 * 가로 스크롤은 건드리지 않는다(넓은 표에서 열이 통째로 밀려 더 헷갈린다).
 */
export function spotlight(name: string): () => void {
  const spots = findSpots(name);
  spots.forEach((el) => el.classList.add(SPOT_CLASS));

  const first = spots[0];
  if (first) {
    const box = first.getBoundingClientRect();
    const hidden = box.top < 64 || box.bottom > window.innerHeight - 24;
    if (hidden) first.scrollIntoView({ block: "center", behavior: "smooth" });
  }
  return () => spots.forEach((el) => el.classList.remove(SPOT_CLASS));
}
